/* eslint-disable camelcase */
import { Maybe, UseModel } from 'core';
import { DSTApiResponseDomainSuggestion } from 'presentation/utils/dstApiResponseTypes';
import { useBasket, Basket } from '@uds/bookends';
import { useCurrency } from '@ux/currency';
import { useDSTContext, DSTquery, useDSTActionContext } from 'presentation/components/pages/CMPage/DSTContext';
import { useEffect, useState } from 'react';
import useCheckDomainAvailability from 'presentation/hooks/useCheckDomainAvailability';
import useAddDomainToBasket from 'application/actions/domain-search/useAddDomainToBasket';
import { KnowledgeType } from '@uds/reactive-knowledge';
import useShowToast from 'application/actions/reactive-knowledge/useShowToast';
import { useAppConfig, useUrls } from '@ux/whitelabel';
import { AddDomainToBasketType } from 'domain/constants/basket';
import { dispatchGAEventSearchMatchDomainName, dispatchGAEventDomainRanking } from 'presentation/utils/domainNameUtils';
import { dispatchGAEvent, link } from '@uds/tagmg';
import useGetSuggestions from 'domain/selectors/dst/useGetSuggestions';
import checkTldFromDomainQuery from 'domain/selectors/dst/checkTldFromDomainQuery';
import { PricingStrategy } from 'presentation/components/common/Price';

import { DomainSearchSuggestionsProps } from './DomainSearchSuggestions';
import { AvailabilityStatus } from '../../domain-search-result-hero/utils';

const isPromo = (oldPrice: Maybe<number>, price: Maybe<number>): boolean => {
  if (oldPrice != null && price != null) {
    return oldPrice > price;
  }
  return false;
};

const isPremium = (item: string) => {
  return item === 'premium' || item === 'registry_premium';
};

const isAd = (item: boolean) => {
  return item === true;
};

type AvailabilityProps = {
  domainName: Maybe<string>;
  query: Maybe<DSTquery>;
  basket: Maybe<Basket>;
  previousStatus: AvailabilityStatus;
};

const getDomainAvailabilityFromBasket = ({ domainName, query, basket, previousStatus }: AvailabilityProps) => {
  if (domainName == null || query == null) {
    return previousStatus;
  }

  const isAddedToBasket = basket?.items?.some((product) => product.domainName === domainName);

  if (isAddedToBasket) {
    return AvailabilityStatus.IN_BASKET;
  }
  return previousStatus;
};

const domainStatusMapper = (status: AvailabilityStatus) => {
  return status ? AvailabilityStatus.AVAILABLE : AvailabilityStatus.UNAVAILABLE;
};

interface Model {
  domainNameOnly: string;
  domainType: string;
  domainExtensionOnly: string;
  firstYearPrice: Maybe<number>;
  oldFirstYearPrice: Maybe<number>;
  parsedPremiumLink: string;
  currency: Maybe<string>;
  hidePrices: boolean;
  domainAvailabilityStatus: AvailabilityStatus;
  hasCaveat: boolean;
  hasPremiumCaveat: boolean;
  hasPremiumBadge: boolean;
  hasAdBadge: boolean;
  hasSalePromoBadge: boolean;
  hasSaleSavingsText: boolean;
  analytics: Maybe<string>;
  pricingStrategy: PricingStrategy;
}

interface Operations {
  handleAddToBasket: () => void;
  handleViewBasket: () => void;
}

export interface DomainSearchSuggestionProps extends DomainSearchSuggestionsProps {
  item: DSTApiResponseDomainSuggestion;
  index: number;
}

export default function useDomainSearchSuggestion({
  item,
  text,
  index,
}: DomainSearchSuggestionProps): UseModel<Model, Operations> {
  const { priceInterval } = useAppConfig('dst');
  const showToast = useShowToast();
  const setQuery = useDSTActionContext();
  const { data: basket } = useBasket();
  const currency = useCurrency();
  const { data: getSuggestionsResponse } = useGetSuggestions();
  const query = useDSTContext();

  const searchPhrase = getSuggestionsResponse?.body?.exactDomain?.search_phrase;
  const domainName = item.fqdn;
  const domainType = item.type;
  const domainPremium = isPremium(item.inventory);
  const domainAd = isAd(item.is_ad);
  const domainSalePromo = isPromo(item.oldPrice?.gross, item.price.gross);
  const domainPrice = item.price.gross;
  const domainExtension = item.extension;

  const [domainAvailabilityStatus, setDomainAvailabilityStatus] = useState(() => {
    return getDomainAvailabilityFromBasket({
      domainName,
      query,
      basket,
      previousStatus: AvailabilityStatus.UNVERIFIED,
    });
  });

  const onError = () => {
    showToast('usf.addToBasket.failure', KnowledgeType.ERK);
    setDomainAvailabilityStatus(AvailabilityStatus.UNAVAILABLE);
  };

  const [hidePrices, setHidePrices] = useState(domainAvailabilityStatus === AvailabilityStatus.IN_BASKET);

  const { refetch } = useCheckDomainAvailability(domainName);
  const { mutateAsync: addToBasket } = useAddDomainToBasket({
    onMutate: () => {
      setQuery({ type: 'DISABLE_CTA_BUTTONS' });
    },
    onError,
    onSuccess: () => {
      showToast('usf.addToBasket.success');
    },
    onSettled: () => {
      setQuery({ type: 'ENABLE_CTA_BUTTONS' });
    },
  });
  const { basket: basketURL } = useUrls();

  const checkDomainAvailability = async () => {
    try {
      setHidePrices(true);
      setDomainAvailabilityStatus(AvailabilityStatus.VERIFYING);
      const resp = await refetch();
      // @ts-ignore: error after upgrading react-query v3
      const status = domainStatusMapper(resp);
      setDomainAvailabilityStatus(status);

      if (status === AvailabilityStatus.AVAILABLE) {
        addToBasket({ fqdn: domainName, type: AddDomainToBasketType.DOMAINS, fqdnType: domainType, tld: domainExtension, price: domainPrice });
      } else {
        showToast('dst.exactMatch.v2.unavailable', KnowledgeType.ERK);
      }
    } catch (error) {
      onError();
    }
  };

  const dispatchGAEventDomainExtension = () => {
    const tealiumPayloadDomainExtension: Record<string, string> = {};
    tealiumPayloadDomainExtension['action'] = 'click';
    tealiumPayloadDomainExtension['category'] = 'Basket';
    tealiumPayloadDomainExtension['label'] = 'Domain Extension';

    tealiumPayloadDomainExtension['domain_extension'] = `.${item.extension}`;
    link(tealiumPayloadDomainExtension);
  };

  const dispatchGAEventDomainExtensionSearched = () => {
    const tealiumPayloadDomainExtensionSearched: Record<string, string> = {};
    tealiumPayloadDomainExtensionSearched['action'] = 'click';
    tealiumPayloadDomainExtensionSearched['category'] = 'Basket';
    tealiumPayloadDomainExtensionSearched['label'] = 'Domain Extension Searched';

    tealiumPayloadDomainExtensionSearched['domain_extension_searched'] = checkTldFromDomainQuery(query.domainName);
    link(tealiumPayloadDomainExtensionSearched);
  };

  useEffect(() => {
    setDomainAvailabilityStatus((previousStatus) =>
      getDomainAvailabilityFromBasket({ domainName, query, basket, previousStatus }),
    );
  }, [domainName, query, basket]);

  const dispatchGAEventDomainTypeAdd = () => {
    if (text.premium.enabled && !!text.premium.badgeLabel) {
      const tealiumPayload: Record<string, string | boolean> = { domain_type_premium: '1' };
      tealiumPayload['action'] = 'click';
      tealiumPayload['category'] = 'Basket';
      tealiumPayload['label'] = 'Premium Domain';
      tealiumPayload['domain_type_premium'] = domainPremium ? '1' : '0';
      link(tealiumPayload);
    }
    if (text.domain.sale?.badge) {
      const tealiumPayload: Record<string, string | boolean> = { domain_type_offer: '1' };
      tealiumPayload['action'] = 'click';
      tealiumPayload['category'] = 'Basket';
      tealiumPayload['label'] = 'Offer';
      tealiumPayload['domain_type_offer'] = domainSalePromo ? '1' : '0';
      link(tealiumPayload);
    }
    if (text.ad.enabled && !!text.ad.badgeLabel) {
      const tealiumPayload: Record<string, string | boolean> = { domain_type_ads_promoted: '1' };
      tealiumPayload['action'] = 'click';
      tealiumPayload['category'] = 'Basket';
      tealiumPayload['label'] = 'Ad/Promoted';
      tealiumPayload['domain_type_ads_promoted'] = domainAd ? '1' : '0';
      link(tealiumPayload);
    }
    dispatchGAEventSearchMatchDomainName(item.name_without_extension, searchPhrase);
  };

  return {
    models: {
      domainNameOnly: item.name_without_extension,
      domainExtensionOnly: item.extension,
      domainType: item.type,
      firstYearPrice: item.price?.display,
      oldFirstYearPrice: item.oldPrice?.display,
      parsedPremiumLink: text.premium.caveat,
      currency,
      hidePrices,
      domainAvailabilityStatus,
      hasCaveat: !!text.domain.sale?.badge && !!item?.legalText && !domainPremium,
      hasPremiumCaveat: domainPremium && text.premium?.enabled && !!text.premium?.caveat,
      hasPremiumBadge: text.premium.enabled && !!text.premium.badgeLabel && domainPremium,
      hasAdBadge: text.ad.enabled && !!text.ad.badgeLabel && domainAd,
      hasSalePromoBadge: !!text.domain.sale?.badge && domainSalePromo,
      hasSaleSavingsText: !!text.domain.sale?.savings && domainSalePromo,
      analytics: text.domain.CTA.analytics,
      pricingStrategy: priceInterval,
    },
    operations: {
      handleAddToBasket: () => {
        dispatchGAEvent({
          hitType: 'event',
          eventCategory: 'Basket',
          eventAction: 'click',
          eventLabel: text.domain.CTA.analytics ?? 'dst-add-suggestions',
        });
        dispatchGAEventDomainTypeAdd();
        dispatchGAEventDomainRanking(index);
        checkDomainAvailability();
        dispatchGAEventDomainExtension();
        dispatchGAEventDomainExtensionSearched();
      },
      handleViewBasket: () => {
        dispatchGAEvent({
          hitType: 'event',
          eventCategory: 'Basket',
          eventAction: 'view',
          eventLabel: text.checkout.CTA.analytics ?? 'dst-checkout-suggestions',
        });
        window.location.href = basketURL;
      },
    },
  };
}
